/* eslint-disable react/jsx-no-bind */
import React, { useState, useRef } from 'react';
import classNames from 'classnames';
import useMeasure from 'react-use/lib/useMeasure';
import ClassReference from 'react-class-references';

import { useCountry, useScrolledPastSpy } from '../../hooks';
import mapNavProps from '../../utils/map-nav-props';

import HeaderContext from './context';
import headerPropTypes from './prop-types';

import { localiseUrl } from 'src/common/utils/format-localised-url';

import { Nav, NavItem, NavList, ExpandedNavItem, ExpandedNavList } from './Nav';
import AccountAction from './AccountAction';
import ReactFromPrismic from '../ReactFromPrismic';
import CountryToggle from '../Header/CountryToggle';
import Basket from '../Header/Basket';
import { Hamburger, WonderblyLogo } from '../SVGs';
import OldHeader from '../Header';
import FlashMessages from '../FlashMessages';

import './Header.scss';

const supportsSticky =
  typeof CSS !== 'undefined' &&
  CSS.supports &&
  (CSS.supports('position: sticky') ||
    CSS.supports('position: -webkit-sticky'));

const Header = ({
  accountLabel,
  cartLabel,
  changeCountryLabel,
  model: { countryModel },
  nav,
  navigation: { backButtonText }
}) => {
  const [navExpanded, setNavExpanded] = useState(false);
  const [setHeaderRef, { height }] = useMeasure();

  const spacerRef = useRef();
  const { scrolledPast } = useScrolledPastSpy(spacerRef, { throttle: 500 });

  const homepageUrl = localiseUrl(countryModel, '/');

  return (
    /* Because the header is sticky, once we scroll past the bottom
       header-ii-spacer, we can assume that header-ii is scrolling. */
    <HeaderContext.Provider value={{ isScrolling: scrolledPast, backButtonText: backButtonText }}>
      <div className="header-ii-spacer" />
      <header
        className={classNames('header-ii', {
          'header-ii--nav-expanded': navExpanded
        })}
        ref={setHeaderRef}>
        <div className="header-ii__inner">
          <div className="container-guttered-on-desktop-up">
            <div className="header-ii__body">
              <a
                className="header-ii__desktop-logo"
                href={homepageUrl}>
                <WonderblyLogo />
              </a>

              <div className="header-ii__nav">{nav}</div>

              <div className="header-ii__content">
                <div className="header-ii__primary-content">
                  <button
                    className="header-ii__nav-toggle"
                    onClick={e => {
                      e.stopPropagation();
                      setNavExpanded(!navExpanded);
                    }}>
                    <div className="header-ii__nav-toggle__hamburger">
                      <Hamburger />
                    </div>
                  </button>
                </div>

                <a className="header-ii__mobile-logo" href={homepageUrl}>
                  <WonderblyLogo />
                </a>

                <div className="header-ii__secondary-content">
                  <div className="header-ii__secondary-content__inner">
                    <div className="header-ii__secondary-content__action">
                      <CountryToggle
                        isFlush
                        changeCountryLink={
                          changeCountryLabel || 'Other country'
                        }
                        countryHeading={
                          changeCountryLabel || 'Choose your country'
                        }
                      />
                    </div>
                    <div className="header-ii__secondary-content__action">
                      <AccountAction country={countryModel}>
                        {accountLabel || 'My Orders'}
                      </AccountAction>
                    </div>
                    <div className="header-ii__secondary-content__action">
                      <Basket
                        country={countryModel}
                        isFlush
                        isSolo
                        linkText={cartLabel || 'Cart'}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="header-ii-spacer" ref={spacerRef} />

      <div
        className="header-ii-appendix"
        style={{
          top: supportsSticky ? height : 0
        }}>
        <FlashMessages />
      </div>

      <div
        className={classNames('header-ii-blackout', {
          'header-ii-blackout--visible': navExpanded
        })}
        onClick={() => navExpanded && setNavExpanded(false)}
      />

      {navExpanded && (
        <ClassReference
          element={document.body}
          classNameToAdd="u-prevent-scroll"
        />
      )}
    </HeaderContext.Provider>
  );
};

Header.propTypes = headerPropTypes;

// Define nav as a prop, so it doesn't get re-rendered when header resizes
const HeaderWithNav = props => {
  if (!props.model.prismicHeaderModel) {
    return <OldHeader />;
  }
  const country = useCountry();
  return (
    <Header
      {...props}
      nav={
        <ReactFromPrismic
          entry={{ ...props.navigation, _type: 'nav' }}
          mapping={{
            nav: Nav,
            nav_list: NavList,
            nav_item: NavItem,
            expanded_nav_item: ExpandedNavItem,
            expanded_nav_list: ExpandedNavList
          }}
          mapAdditionalProps={(prop, type) => mapNavProps(prop, country, type)}
        />
      }
    />
  );
};

HeaderWithNav.propTypes = headerPropTypes;

export default HeaderWithNav;
